import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { Card, ProgressBar } from '../../components';
import type { AppData, Category } from '../../data/types';
import { cn } from '../../lib/cn';
import {
  calculate503020,
  calculateDailyAllowance,
  calculatePYF,
  calculateZeroBased,
  getPeriodSummary,
} from '../../lib/budget';
import { formatCurrency } from '../../lib/currency';
import { springGentle } from '../../tokens';

type Props = {
  data: AppData;
  today: Date;
  /** Tapped a Zero-Based category — open the allocation editor. */
  onEditAllocation?: (categoryId: string) => void;
};

/**
 * Top-of-Plan-tab budget model visualization. Each branch is its own
 * sub-component so the file reads top-down — Daily / 50-30-20 / PYF /
 * Zero-Based / Conscious.
 */
export function BudgetModelViz({ data, today, onEditAllocation }: Props) {
  switch (data.profile.budgetModel) {
    case 'daily':
      return <DailyViz data={data} today={today} />;
    case '503020':
      return <FiftyThirtyTwentyViz data={data} today={today} />;
    case 'pyf':
      return <PYFViz data={data} today={today} />;
    case 'zero':
      return (
        <ZeroBasedViz
          data={data}
          today={today}
          onEditAllocation={onEditAllocation}
        />
      );
    case 'conscious':
      return <ConsciousViz data={data} today={today} />;
  }
}

// ---------- Daily Allowance ----------

function DailyViz({ data, today }: Props) {
  const r = calculateDailyAllowance(data, today);
  const progress =
    r.periodIncome > 0
      ? Math.min(1, r.spentThisPeriod / r.periodIncome)
      : 0;
  const tone =
    r.remainingThisPeriod < 0 ? 'negative' : ('accent' as const);
  return (
    <Card elevation="lg" className="mb-section-gap p-6 rounded-[20px]">
      <Caption>Period budget</Caption>
      <div className="mt-2 flex items-baseline gap-3">
        <span
          className={cn(
            'text-[36px] font-light font-mono tabular-nums',
            r.remainingThisPeriod < 0 ? 'text-negative' : 'text-text-primary',
          )}
        >
          {formatCurrency(r.remainingThisPeriod, {
            currency: data.profile.currency,
          })}
        </span>
        <span className="text-[14px] text-text-secondary">
          left of{' '}
          {formatCurrency(r.periodIncome, {
            currency: data.profile.currency,
          })}
        </span>
      </div>
      <div className="mt-5">
        <ProgressBar value={progress} tone={tone} />
        <div className="mt-3 flex items-baseline justify-between text-[12px] text-text-tertiary">
          <span>
            Day {r.period.dayInPeriod} of {r.period.totalDays}
          </span>
          <span className="font-mono tabular-nums">
            {formatCurrency(r.spentThisPeriod, {
              currency: data.profile.currency,
            })}{' '}
            spent
          </span>
        </div>
      </div>
    </Card>
  );
}

// ---------- 50 / 30 / 20 ----------

const BUCKET_COLOR = {
  needs: 'rgb(var(--accent))',
  wants: '#AF52DE',
  savings: 'rgb(var(--positive))',
} as const;

function FiftyThirtyTwentyViz({ data, today }: Props) {
  const r = calculate503020(data, today);
  const total = r.needs.allocated + r.wants.allocated + r.savings.allocated;
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);

  return (
    <Card elevation="lg" className="mb-section-gap p-6 rounded-[20px]">
      <Caption>50 / 30 / 20 this period</Caption>
      {/* Stacked allocation bar */}
      <div
        className="mt-3 flex w-full h-3 rounded-pill overflow-hidden bg-surface-secondary"
        role="presentation"
      >
        <motion.span
          initial={{ width: 0 }}
          animate={{ width: `${pct(r.needs.allocated)}%` }}
          transition={springGentle}
          style={{ backgroundColor: BUCKET_COLOR.needs }}
        />
        <motion.span
          initial={{ width: 0 }}
          animate={{ width: `${pct(r.wants.allocated)}%` }}
          transition={springGentle}
          style={{ backgroundColor: BUCKET_COLOR.wants }}
        />
        <motion.span
          initial={{ width: 0 }}
          animate={{ width: `${pct(r.savings.allocated)}%` }}
          transition={springGentle}
          style={{ backgroundColor: BUCKET_COLOR.savings }}
        />
      </div>
      <div className="mt-5 grid grid-cols-3 gap-3">
        <BucketTile
          label="Needs"
          color={BUCKET_COLOR.needs}
          spent={r.needs.spent}
          allocated={r.needs.allocated}
          progress={r.needs.progress}
          currency={data.profile.currency}
        />
        <BucketTile
          label="Wants"
          color={BUCKET_COLOR.wants}
          spent={r.wants.spent}
          allocated={r.wants.allocated}
          progress={r.wants.progress}
          currency={data.profile.currency}
        />
        <BucketTile
          label="Savings"
          color={BUCKET_COLOR.savings}
          spent={r.savings.spent}
          allocated={r.savings.allocated}
          progress={r.savings.progress}
          currency={data.profile.currency}
        />
      </div>
    </Card>
  );
}

function BucketTile({
  label,
  color,
  spent,
  allocated,
  progress,
  currency,
}: {
  label: string;
  color: string;
  spent: number;
  allocated: number;
  progress: number;
  currency: string;
}) {
  const over = spent > allocated && allocated > 0;
  return (
    <div>
      <div className="flex items-center gap-1.5">
        <span
          className="w-2 h-2 rounded-pill"
          style={{ backgroundColor: color }}
        />
        <span className="text-[12px] font-medium text-text-secondary">
          {label}
        </span>
      </div>
      <div
        className={cn(
          'mt-1 text-[15px] font-mono tabular-nums',
          over ? 'text-negative' : 'text-text-primary',
        )}
      >
        {formatCurrency(spent, { currency })}
      </div>
      <div className="text-[11px] text-text-tertiary font-mono tabular-nums">
        / {formatCurrency(allocated, { currency })}
      </div>
      <div className="mt-2 h-[3px] rounded-pill bg-surface-secondary overflow-hidden">
        <motion.span
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(100, progress * 100)}%` }}
          transition={springGentle}
          className="block h-full rounded-pill"
          style={{ backgroundColor: over ? 'rgb(var(--negative))' : color }}
        />
      </div>
    </div>
  );
}

// ---------- Pay Yourself First ----------

function PYFViz({ data, today }: Props) {
  const r = calculatePYF(data, today);
  const currency = data.profile.currency;
  return (
    <Card elevation="lg" className="mb-section-gap p-5 rounded-3xl">
      <Caption>Pay yourself first</Caption>

      {/* Ring centered above the stat grid. */}
      <div className="mt-4 mb-5 flex justify-center">
        <CircularProgress progress={r.progress} size={128} strokeWidth={10}>
          <div className="text-center">
            <div className="text-[32px] font-extralight font-mono tabular-nums text-emerald-600 dark:text-emerald-500 leading-none">
              {Math.round(r.progress * 100)}%
            </div>
            <span className="inline-block mt-1.5 rounded-pill px-2 py-[2px] bg-emerald-600/10 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 text-[10px] font-semibold uppercase tracking-[0.06em] leading-none">
              On pace
            </span>
          </div>
        </CircularProgress>
      </div>

      {/* Squircle stat grid: 2 cols × 2 rows + a full-width "Per year" row. */}
      <div className="grid grid-cols-2 gap-2">
        <StatTile label="This period" value={r.savedThisPeriod} currency={currency} tone="positive" />
        <StatTile label="Goal to date" value={r.goalToDate} currency={currency} />
        <StatTile label="Hourly rate" value={r.hourlyRate} currency={currency} />
        <StatTile label="Per day" value={r.dailyGoal} currency={currency} />
        <StatTile
          label="Per year"
          value={r.yearlyGoal}
          currency={currency}
          className="col-span-2"
        />
      </div>
    </Card>
  );
}

function StatTile({
  label,
  value,
  currency,
  tone,
  className,
}: {
  label: string;
  value: number;
  currency: string;
  tone?: 'positive';
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-[20px] p-3 bg-bg dark:bg-surface-secondary',
        className,
      )}
    >
      <div className="text-[12px] font-medium text-text-tertiary">
        {label}
      </div>
      <div
        className={cn(
          'mt-0.5 text-[17px] font-medium font-mono tabular-nums',
          tone === 'positive'
            ? 'text-emerald-600 dark:text-emerald-500'
            : 'text-text-primary',
        )}
      >
        {formatCurrency(value, { currency })}
      </div>
    </div>
  );
}

function CircularProgress({
  progress,
  size,
  strokeWidth,
  children,
}: {
  progress: number;
  size: number;
  strokeWidth: number;
  children: ReactNode;
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(1, progress));
  const offset = circumference * (1 - clamped);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-emerald-600/20 dark:text-emerald-500/20"
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          className="text-emerald-600 dark:text-emerald-500"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={springGentle}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        {children}
      </div>
    </div>
  );
}

// ---------- Zero-Based ----------

function ZeroBasedViz({
  data,
  today,
  onEditAllocation,
}: Props & { onEditAllocation?: (categoryId: string) => void }) {
  const r = calculateZeroBased(data, today);
  const unbudgeted = r.toAssignMonthly;
  const tone =
    unbudgeted > 0
      ? 'text-accent'
      : unbudgeted < 0
        ? 'text-negative'
        : 'text-positive';
  const headline =
    unbudgeted > 0
      ? 'To assign'
      : unbudgeted < 0
        ? 'Over-assigned'
        : 'All assigned';
  // Allocated categories first, then unallocated ones — every expense
  // category renders so the user can tap any to set or change its
  // monthly amount.
  const cats = [...r.perCategory].sort((a, b) => {
    if (a.allocated !== b.allocated) return b.allocated - a.allocated;
    return b.spent - a.spent;
  });

  return (
    <Card elevation="lg" className="mb-section-gap p-6 rounded-[20px]">
      <Caption>Zero-based</Caption>
      <div className="mt-2 text-[13px] font-medium text-text-secondary">
        {headline}
      </div>
      <div
        className={cn(
          'mt-1 text-[32px] font-light font-mono tabular-nums',
          tone,
        )}
      >
        {formatCurrency(Math.abs(unbudgeted), { currency: data.profile.currency })}
      </div>
      {cats.length === 0 ? (
        <p className="mt-4 text-[13px] text-text-secondary">
          Add an expense category in Settings to start allocating.
        </p>
      ) : (
        <div className="mt-5 flex flex-col gap-3">
          <p className="text-[12px] text-text-secondary -mt-1">
            Tap a category to set or change its monthly allocation.
          </p>
          {cats.map((c) => {
            const over = c.spent > c.allocated && c.allocated > 0;
            const unallocated = c.allocated <= 0;
            const interactive = !!onEditAllocation;
            const inner = (
              <>
                <div className="flex items-baseline justify-between mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="w-2 h-2 rounded-pill shrink-0"
                      style={{ backgroundColor: c.color }}
                    />
                    <span className="text-[13px] font-medium text-text-primary truncate">
                      {c.name}
                    </span>
                  </div>
                  {unallocated ? (
                    <span className="text-[12px] text-accent font-medium shrink-0">
                      Tap to set
                    </span>
                  ) : (
                    <span className="text-[12px] font-mono tabular-nums text-text-secondary shrink-0">
                      {formatCurrency(c.spent, {
                        currency: data.profile.currency,
                      })}{' '}
                      <span className="text-text-tertiary">
                        /{' '}
                        {formatCurrency(c.allocated, {
                          currency: data.profile.currency,
                        })}
                      </span>
                    </span>
                  )}
                </div>
                <div className="h-[3px] rounded-pill bg-surface-secondary overflow-hidden">
                  <motion.span
                    initial={{ width: 0 }}
                    animate={{
                      width: `${Math.min(100, c.progress * 100)}%`,
                    }}
                    transition={springGentle}
                    className={cn(
                      'block h-full rounded-pill',
                      over ? 'bg-negative' : 'bg-accent',
                    )}
                  />
                </div>
              </>
            );
            if (interactive) {
              return (
                <motion.button
                  key={c.categoryId}
                  type="button"
                  onClick={() => onEditAllocation?.(c.categoryId)}
                  whileTap={{ scale: 0.98 }}
                  transition={springGentle}
                  className="text-left w-full"
                >
                  {inner}
                </motion.button>
              );
            }
            return <div key={c.categoryId}>{inner}</div>;
          })}
        </div>
      )}
    </Card>
  );
}

// ---------- Conscious Spending ----------

function ConsciousViz({ data, today }: Props) {
  const summary = getPeriodSummary(data, today);
  const period = summary.period;

  // Period transactions only.
  const periodTx = data.transactions.filter((t) => {
    if (t.type !== 'expense') return false;
    const d = new Date(t.date);
    return d >= period.start && d <= period.end;
  });
  const byCat = new Map<string, number>();
  for (const t of periodTx) {
    byCat.set(t.categoryId, (byCat.get(t.categoryId) ?? 0) + t.amount);
  }
  const top = [...byCat.entries()]
    .map(([id, amount]) => {
      const cat = data.categories.find((c) => c.id === id);
      return {
        id,
        amount,
        name: cat?.name ?? 'Uncategorized',
        color: cat?.color ?? '#AEAEB2',
      };
    })
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 5);
  const max = top[0]?.amount ?? 0;

  return (
    <Card elevation="lg" className="mb-section-gap p-6 rounded-[20px]">
      <Caption>Conscious spending</Caption>
      <div className="mt-3 grid grid-cols-3 gap-3">
        <Stat
          label="In"
          value={summary.income}
          tone="text-positive"
          currency={data.profile.currency}
        />
        <Stat
          label="Out"
          value={summary.expenses}
          tone="text-negative"
          currency={data.profile.currency}
        />
        <Stat
          label="Net"
          value={summary.net}
          tone={
            summary.net >= 0
              ? 'text-text-primary'
              : 'text-negative'
          }
          currency={data.profile.currency}
        />
      </div>
      {top.length === 0 ? (
        <p className="mt-5 text-[13px] text-text-secondary">
          Track a few transactions to see where your money is going.
        </p>
      ) : (
        <div className="mt-5">
          <div className="text-[13px] font-semibold text-text-secondary mb-3">
            Top spending
          </div>
          <div className="flex flex-col gap-2.5">
            {top.map((row) => (
              <div key={row.id}>
                <div className="flex items-baseline justify-between mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="w-2 h-2 rounded-pill shrink-0"
                      style={{ backgroundColor: row.color }}
                    />
                    <span className="text-[13px] font-medium text-text-primary truncate">
                      {row.name}
                    </span>
                  </div>
                  <span className="text-[12px] font-mono tabular-nums text-text-secondary">
                    {formatCurrency(row.amount, {
                      currency: data.profile.currency,
                    })}
                  </span>
                </div>
                <div className="h-[3px] rounded-pill bg-surface-secondary overflow-hidden">
                  <motion.span
                    initial={{ width: 0 }}
                    animate={{
                      width: `${max > 0 ? (row.amount / max) * 100 : 0}%`,
                    }}
                    transition={springGentle}
                    className="block h-full rounded-pill"
                    style={{ backgroundColor: row.color }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function Stat({
  label,
  value,
  tone,
  currency,
}: {
  label: string;
  value: number;
  tone: string;
  currency: string;
}) {
  return (
    <div>
      <div className="text-[12px] font-medium text-text-tertiary">
        {label}
      </div>
      <div className={cn('mt-1 text-[18px] font-mono font-light tabular-nums', tone)}>
        {formatCurrency(value, { currency })}
      </div>
    </div>
  );
}

// ---------- Shared bits ----------

function Caption({ children }: { children: ReactNode }) {
  return (
    <div className="text-[13px] font-semibold text-text-secondary">
      {children}
    </div>
  );
}

// Avoid `Category` unused-import warning under noUnusedParameters.
export type { Category };
